"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { dictionaries, interpolate, LOCALE_KEY, THEME_KEY, type Dict, type Locale, type Theme } from "@/lib/i18n";

type Prefs = {
  locale: Locale;
  theme: Theme;
  t: Dict;
  setLocale: (locale: Locale) => void;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  tf: (template: string, vars: Record<string, string | number>) => string;
};

const PrefsContext = createContext<Prefs | null>(null);

function applyDom(locale: Locale, theme: Theme) {
  const root = document.documentElement;
  root.lang = locale;
  root.dir = locale === "he" ? "rtl" : "ltr";
  root.classList.toggle("dark", theme === "dark");
}

export function PrefsProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>("he");
  const [theme, setThemeState] = useState<Theme>("light");

  useEffect(() => {
    const storedLocale = (localStorage.getItem(LOCALE_KEY) as Locale | null) || "he";
    const storedTheme =
      (localStorage.getItem(THEME_KEY) as Theme | null) ||
      (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
    const loc: Locale = storedLocale === "en" ? "en" : "he";
    const th: Theme = storedTheme === "dark" ? "dark" : "light";
    setLocaleState(loc);
    setThemeState(th);
    applyDom(loc, th);
  }, []);

  const persistLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    localStorage.setItem(LOCALE_KEY, next);
    applyDom(next, (document.documentElement.classList.contains("dark") ? "dark" : "light") as Theme);
    fetch("/api/settings", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reportsSettings: { alertLanguage: next } }),
    }).catch(() => undefined);
  }, []);

  const persistTheme = useCallback((next: Theme) => {
    setThemeState(next);
    localStorage.setItem(THEME_KEY, next);
    applyDom((document.documentElement.lang === "en" ? "en" : "he") as Locale, next);
  }, []);

  const setLocale = useCallback(
    (next: Locale) => {
      persistLocale(next);
    },
    [persistLocale],
  );

  const setTheme = useCallback(
    (next: Theme) => {
      persistTheme(next);
    },
    [persistTheme],
  );

  const toggleTheme = useCallback(() => {
    setThemeState((cur) => {
      const next: Theme = cur === "dark" ? "light" : "dark";
      localStorage.setItem(THEME_KEY, next);
      applyDom((document.documentElement.lang === "en" ? "en" : "he") as Locale, next);
      return next;
    });
  }, []);

  const value = useMemo<Prefs>(
    () => ({
      locale,
      theme,
      t: dictionaries[locale] ?? dictionaries.he,
      setLocale,
      setTheme,
      toggleTheme,
      tf: interpolate,
    }),
    [locale, theme, setLocale, setTheme, toggleTheme],
  );

  return <PrefsContext.Provider value={value}>{children}</PrefsContext.Provider>;
}

export function usePrefs() {
  const ctx = useContext(PrefsContext);
  if (!ctx) throw new Error("usePrefs must be used within PrefsProvider");
  return ctx;
}
